import { useMemo } from 'react';

import { type ProductInformation, type Sku, useProductCatalogRepo } from '@hotjar/product-catalog';
import { type Site } from '@hotjar/site';
import { useSubscriptionInfo } from '@hotjar/subscription-management';
import { isBillingV3Enabled } from '@hotjar/webapp/domains/billing/features';
import { useServiceProducts } from '@hotjar/service-management';

interface Props {
  site: Site | undefined | null;
  selectedSkus: Array<Sku>;
}

interface UseCanSelfServeUpsell {
  isLoading: boolean;
  canSelfServe: boolean;
}

/**
 * Expects a site model object and an array of selected SKUs and returns whether or not an upsell with those SKUs can
 * be self-served.
 *
 * Upsells are considered self-servable if all of the following conditions are true:
 *   * All selected allowances are self-servable.
 *   * All selected allowances are higher in cost than the entity's effective allowance of the same product.
 *
 * If any individual allowance fails any condition, the whole upsell is considered non-self-servable.
 *
 * For now, this hook doesn't care about feature groups / tiers / plans. See more detailed comment below.
 */
export const useCanSelfServeUpsell = ({ site, selectedSkus }: Props): UseCanSelfServeUpsell => {
  const billingV3 = isBillingV3Enabled(site?.account);

  const { data: subscriptionInfo, isLoading: isLoadingSubscription } = useSubscriptionInfo({
    enabled: billingV3,
  });
  const { subscription } = subscriptionInfo?.customer || {};

  const { getProductBySku, isLoading: isLoadingProductCatalogRepo } = useProductCatalogRepo({
    billingPeriod: subscription?.billingPeriod ?? 'MONTHLY',
    currency: subscriptionInfo?.customer?.currency ?? 'EUR',
    enabled: billingV3 && !!site,
  });

  const { data: serviceProducts, isLoading: isLoadingServiceProducts } = useServiceProducts(
    site?.account.id,
  );

  const isLoading =
    isLoadingSubscription || isLoadingProductCatalogRepo || isLoadingServiceProducts;

  const effectiveSkus = useMemo(
    () =>
      isLoading || !serviceProducts
        ? []
        : serviceProducts.effectiveNow
            .filter((serviceProduct) => String(serviceProduct.entity.id) === String(site?.id))
            .map((value) => value.sku),
    [isLoading, serviceProducts, site?.id],
  );

  const effectiveAllowances = useMemo(
    () =>
      isLoading
        ? []
        : effectiveSkus
            .map((sku) => getProductBySku(sku))
            .filter((value): value is ProductInformation => typeof value !== 'undefined'),
    [effectiveSkus, getProductBySku, isLoading],
  );
  const selectedAllowances = useMemo(
    () =>
      isLoading
        ? []
        : selectedSkus
            .map((sku) => getProductBySku(sku))
            .filter((value): value is ProductInformation => typeof value !== 'undefined'),
    [selectedSkus, getProductBySku, isLoading],
  );

  if (isLoading) {
    return {
      isLoading: true,
      canSelfServe: true,
    };
  }

  if (!selectedSkus.length) {
    return {
      isLoading: false,
      canSelfServe: true,
    };
  }

  /**
   * If there's any non-self-servable allowance in the `selectedAllowances` array, immediately return false as the
   * upsell is not self-servable.
   */
  if (selectedAllowances.some(({ allowance }) => !allowance.selfServeEnabled)) {
    return {
      isLoading: false,
      canSelfServe: false,
    };
  }

  return {
    isLoading: false,
    canSelfServe: selectedAllowances.every((selectedAllowance) => {
      const productId = selectedAllowance.product.id;

      // Find effective allowance for the same product
      const effectiveProductAllowance = effectiveAllowances.find(
        (effectiveAllowance) => effectiveAllowance.product.id === productId,
      );

      // If there's no existing effective allowance for this product, we allow self-serving the selected allowance
      if (!effectiveProductAllowance) {
        return true;
      }

      /**
       * TODO: We might want to add an additional check here to verify that the selected allowance's plan is higher in
       * rank than the effective. As in, verify that the user is not switching from Scale to Business. Note that price
       * might not be indicative of this, as there can be more expensive allowances in Business than in Scale.
       * Regardless of the price, switching from Scale to Business is considered a downgrade, and we don't want to allow
       * that for now.
       *
       * Currently, we prevent that in the modals using a different logic that uses the legacy `FeatureGroupEnum` type
       * for comparing the feature groups (plans). It's going to be slightly trickier with Billing v3 as we don't have
       * all existing plans hardcoded. The approach has yet to be figured out.
       */

      return selectedAllowance.allowance.price >= effectiveProductAllowance.allowance.price;
    }),
  };
};
